<template>
  <layout width="540">
    <template slot="header">
      {{ title }}
    </template>

    <template>
      <validation-observer ref="form">
        <v-form>
          <form-subheader title="General info" />
          <validation-provider
            v-slot="{ errors }"
            name="Geotag name"
            rules="required"
          >
            <v-text-field
              v-model.trim="state.name"
              label="Geotag name"
              :error-messages="errors"
            />
          </validation-provider>
          <image-uploader
            label="Photo"
            :src="imageUrl"
            @change="handleChangeImage"
            @clear="handleClearImage"
          />
          <form-subheader title="Position" />
          <form-text v-if="!edit">
            The geotag will be created in the center of the current map
            position. Its position may be changed from the edit item menu.
          </form-text>
          <v-text-field v-model="state.minAltitude" label="Min altitude" />
          <v-text-field v-model="state.maxAltitude" label="Max altitude" />
          <form-subheader title="Address" />
          <v-text-field v-model="state.country" label="Country" />
          <v-text-field v-model="state.city" label="City" />
          <v-text-field v-model="state.street" label="Street" />
          <v-text-field v-model="state.building" label="Building" />
          <v-text-field v-model="state.room" label="Room" />
          <v-text-field v-model="state.postcode" label="Postcode" />
          <form-subheader title="Options" />
          <form-switcher v-model="state.enabled" title="Enabled" />
          <form-switcher
            v-model="state.autoRecalculation"
            title="Automatic recalculation"
          />
          <form-subheader title="Description" />
          <v-textarea
            v-model="state.description"
            no-resize
            rows="1"
            auto-grow
          />
        </v-form>
      </validation-observer>
    </template>
    <template slot="footer">
      <v-spacer />
      <v-btn text color="text-primary" @click.stop="$emit('close')">
        Cancel
      </v-btn>
      <v-btn
        text
        color="primary"
        depressed
        :loading="loading"
        @click.stop="submit"
      >
        {{ submitBtnTitle }}
      </v-btn>
    </template>
  </layout>
</template>

<script>
import Layout from '@/components/popup/PopupLayoutDefault';
import { ref, onMounted, computed } from '@vue/composition-api';
import { usePromise } from 'vue-composable';
import { geotagservice } from '@/modules/geotags/api';
import { imageService, objectPropertyService } from '@/modules/common/api';
import { mediaClient } from '@/provider';
import { required } from 'vee-validate/dist/rules';
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate';

extend('required', {
  ...required,
  message: 'This field is required'
});

export default {
  name: 'GeotagEdit',
  components: {
    Layout,
    ValidationObserver,
    ValidationProvider
  },
  props: {
    geotagId: {
      type: String,
      default: ''
    },
    defaultSize: {
      type: Array,
      default: () => []
    },
    defaultCenter: {
      type: Array,
      default: () => []
    }
  },
  setup(props, { emit, root }) {
    const state = ref({
      schemaId: '',
      name: '',
      description: '',
      imageId: '',
      minAltitude: 0,
      maxAltitude: 0,
      country: '',
      city: '',
      street: '',
      building: '',
      postcode: '',
      room: '',
      enabled: true,
      autoRecalculation: true,
      positionPoints: [],
      positionBaseCoordinate: []
    });

    const form = ref(null);
    const geotag = ref({});

    const edit = computed(() => props.geotagId);
    const title = computed(() => (edit.value ? 'Edit geotag' : 'Add geotag'));
    const submitBtnTitle = computed(() => (edit.value ? 'Save' : 'Add'));

    const imageLoaded = ref(false);

    const imageUrl = computed(() =>
      mediaClient.getImageUrl(state.value.imageId)
    );

    const handleChangeImage = async e => {
      console.log(e);
      state.value.imageId = await imageService.upload(e.target.files[0]);
    };

    const handleClearImage = () => {
      state.value.imageId.value = '';
      imageLoaded.value = false;
    };

    const handleSubmit = async () => {
      form.value.validate().then(async success => {
        if (!success) {
          return;
        }

        if (!edit.value) {
          // create new geotag
          geotag.value = await geotagservice.create({
            name: state.value.name,
            description: state.value.description,
            schemaId: state.value.schemaId,
            enabled: state.value.enabled
          });
          await objectPropertyService.updateValues([
            {
              id: geotag.value.positionPoints.id,
              value: props.defaultSize
            },
            {
              id: geotag.value.positionBaseCoordinate.id,
              value: {
                lon: props.defaultCenter[0] || 0,
                lat: props.defaultCenter[1] || 0
              }
            }
          ]);
        } else {
          await geotagservice.update(props.geotagId, {
            name: state.value.name,
            description: state.value.description,
            enabled: state.value.enabled
          });
        }
        await objectPropertyService.updateValues([
          {
            id: geotag.value.infoImageId.id,
            value: state.value.imageId
          },
          {
            id: geotag.value.addressBuilding.id,
            value: state.value.building
          },
          {
            id: geotag.value.addressCity.id,
            value: state.value.city
          },
          {
            id: geotag.value.addressCountry.id,
            value: state.value.country
          },
          {
            id: geotag.value.addressPostcode.id,
            value: state.value.postcode
          },
          {
            id: geotag.value.addressRoom.id,
            value: state.value.room
          },
          {
            id: geotag.value.addressStreet.id,
            value: state.value.street
          },
          {
            id: geotag.value.positionMinAltitude.id,
            value: Number(state.value.minAltitude) || 0
          },
          {
            id: geotag.value.positionMaxAltitude.id,
            value: Number(state.value.maxAltitude) || 0
          },
          {
            id: geotag.value.optionsAutoRecalculation.id,
            value: Boolean(state.value.autoRecalculation)
          }
        ]);

        if (!edit.value) {
          root.$router.push({
            name: 'geotag_card',
            params: {
              geotagId: geotag.value.id
            }
          });
        }

        emit('close');
      });
    };

    const { loading, exec: submit } = usePromise(handleSubmit, true);

    const init = async () => {
      if (!edit.value) {
        state.value.schemaId = await geotagservice.fetchGeotagSchemaId();
        if (!state.value.schemaId) {
          console.warn(`Can't fetch geotag schema`);
        }
      } else {
        geotag.value = await geotagservice.fetch(props.geotagId);

        const {
          infoImageId,
          name,
          description,
          enabled,
          addressBuilding,
          addressCity,
          addressCountry,
          addressPostcode,
          addressRoom,
          addressStreet,
          positionMinAltitude,
          positionMaxAltitude,
          optionsAutoRecalculation
        } = geotag.value;
        state.value = {
          ...state.value,
          imageId: infoImageId.value,
          name,
          description,
          enabled,
          building: addressBuilding.value,
          city: addressCity.value,
          country: addressCountry.value,
          postcode: addressPostcode.value,
          room: addressRoom.value,
          street: addressStreet.value,
          minAltitude: positionMinAltitude.value,
          maxAltitude: positionMaxAltitude.value,
          autoRecalculation: optionsAutoRecalculation.value
        };
      }
    };
    onMounted(async () => {
      init();
    });

    return {
      state,
      loading,
      title,
      submitBtnTitle,
      submit,
      imageUrl,
      handleClearImage,
      handleChangeImage,
      edit,
      form
    };
  }
};
</script>
